import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { useFeatureFlagEnabled } from "posthog-js/react"

import { useAppSelector } from "@/reducers/hooks"
import { findActivity } from "@/reducers/activitySlice"
import {
  findCurrentTarget,
  findRoute2g,
  findRouteAlertConfig,
  route2gEq,
  RouteStop,
  shouldAlert,
} from "@/reducers/routes2gSlice"
import { selectReferenceRouteStatus } from "@/reducers/referenceRouteStatusesSlice"
import {
  selectLocationJidFromChatJid,
  selectRouteJidFromChatJid,
} from "@/reducers/selectors"
import { selectLocation } from "@/reducers/locationsSelectors"
import { selectEffectiveDriverForChat } from "@/reducers/driverResolutionSelectors"
import { useEnsureCarConfigLoaded } from "@/hooks/useEnsureCarConfigLoaded"
import { formatStopDistance } from "@/components/chatCard/utils"
import {
  HIDE_OFF_ROUTE_STATUS_BADGES_FLAG,
  shouldShowReferenceRouteStatusBadge,
} from "@/lib/referenceRouteStatusBadge"
import { CAR_SPEED_FLAG, carSpeedLabel } from "@/lib/carSpeed"

/** Helper function to determine temperature badge variant */
const getTemperatureVariant = (
  temperature: number | undefined,
  minAlertLevel: string | undefined,
  maxAlertLevel: string | undefined,
): "neutral" | "positive" | "error" | undefined => {
  if (!temperature) return undefined
  if (!minAlertLevel && !maxAlertLevel) {
    return "neutral"
  }
  const min = minAlertLevel ? parseFloat(minAlertLevel) : -Infinity
  const max = maxAlertLevel ? parseFloat(maxAlertLevel) : Infinity
  if (temperature >= min && temperature <= max) {
    return "positive"
  }
  return "error"
}

/**
 * Everything a chat row shows about the truck rather than the conversation:
 * next stop, distance, lateness, tracking, GPS, temperatures and who drives.
 *
 * All of it hangs off the chat JID alone, through slices the client fetches for
 * the whole account at login. That is what lets another dispatcher's chat —
 * which we have never joined and hold no messages for — carry the same badges
 * as our own.
 */
export const useRouteBadges = (chatJid: string) => {
  const routeJid = useAppSelector(selectRouteJidFromChatJid(chatJid))
  useEnsureCarConfigLoaded(routeJid)
  const { t } = useTranslation()

  const hideOffRouteStatusBadges = Boolean(
    useFeatureFlagEnabled(HIDE_OFF_ROUTE_STATUS_BADGES_FLAG),
  )

  const showSpeed = Boolean(useFeatureFlagEnabled(CAR_SPEED_FLAG))

  const locationJid = useAppSelector(selectLocationJidFromChatJid(chatJid))
  const currentLocation = useAppSelector(selectLocation(locationJid))

  const driverParticipantSelector = useMemo(
    () => selectEffectiveDriverForChat(chatJid),
    [chatJid],
  )
  const driverParticipant = useAppSelector(
    driverParticipantSelector,
    shallowEqual,
  )

  const activitySelector = useMemo(
    () => findActivity(routeJid?.toString()),
    [routeJid],
  )
  const activity = useAppSelector(activitySelector, shallowEqual)

  const routeSelector = useMemo(
    () => findRoute2g(routeJid?.toString()),
    [routeJid],
  )
  const route = useAppSelector(routeSelector, route2gEq)

  const referenceRouteStatusSelector = useMemo(
    () => selectReferenceRouteStatus(routeJid?.toString()),
    [routeJid],
  )
  const referenceRouteStatus = useAppSelector(referenceRouteStatusSelector)

  const routeAlertConfigSelector = useMemo(
    () => findRouteAlertConfig(driverParticipant?.jid),
    [driverParticipant?.jid],
  )
  const routeAlertConfig = useAppSelector(routeAlertConfigSelector)

  // Route and navigation data
  const displayedStop = useMemo(
    () => findCurrentTarget(route?.stops),
    [route?.stops],
  )

  const distance = useMemo(
    () => (displayedStop ? formatStopDistance(displayedStop, t) : undefined),
    [displayedStop, t],
  )

  const isLate = useMemo(
    () => route?.stops?.some((stop: RouteStop) => shouldAlert(stop)) ?? false,
    [route?.stops],
  )

  const isNextStopLate = displayedStop ? shouldAlert(displayedStop) : false

  // Route status flags
  const hasRoute = !!displayedStop
  const showAlert = !!displayedStop?.scheduled_arrival_time
  const isTracked = !!referenceRouteStatus
  const isOffRoute = referenceRouteStatus?.isOffRoute ?? false
  const showRouteStatusBadge = shouldShowReferenceRouteStatusBadge({
    isTracked,
    isOffRoute,
    hideOffRouteStatusBadges,
  })

  const temperatureMain = currentLocation?.temperatureMain
  const temperatureAdditional = currentLocation?.temperatureAdditional

  const temperatureMainVariant = useMemo(
    () =>
      getTemperatureVariant(
        temperatureMain,
        routeAlertConfig?.temperature_main_alert_level_min,
        routeAlertConfig?.temperature_main_alert_level_max,
      ),
    [temperatureMain, routeAlertConfig],
  )

  const temperatureAdditionalVariant = useMemo(
    () =>
      getTemperatureVariant(
        temperatureAdditional,
        routeAlertConfig?.temperature_additional_alert_level_min,
        routeAlertConfig?.temperature_additional_alert_level_max,
      ),
    [temperatureAdditional, routeAlertConfig],
  )

  return {
    /** Not a badge, but every caller needs it to name and sign the row. */
    driverParticipant,

    referenceRouteStatus,
    activity,
    isDriving: activity?.specific === "driving",
    isLate,
    isNextStopLate,
    distance,
    hasRoute,
    isTracked,
    isOffRoute,
    showRouteStatusBadge,
    nextStop: displayedStop,
    showAlert,
    hasGPS: !!currentLocation,
    stationarySince: currentLocation?.stationarySince,
    speedLabel: showSpeed ? carSpeedLabel(currentLocation) : undefined,
    temperatureMain,
    temperatureAdditional,
    temperatureMainVariant,
    temperatureAdditionalVariant,
  }
}

export default useRouteBadges
